import { useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useModals, ModalNames } from 'components/modals';
import { ORDER_STATUSES } from 'src/constants';
import { ConsumerUser, ConsumerOrder } from '../../account.types';
import { Orders } from './use-orders-for-consumer/types';

const { OPEN, CONFIRMED, IN_TRANSIT } = ORDER_STATUSES;
const VIEWABLE_STATUSES = new Set([OPEN, CONFIRMED, IN_TRANSIT]);

type UseOrderListParams = {
  loading: boolean;
  orders: Orders;
  user: ConsumerUser;
};

type UseOrderListReturn = {
  viewableStatuses: Set<string>;
  showLoadingSpinner: boolean;
  handleViewOrder: (order: ConsumerOrder, canViewStatus: boolean) => void;
};

function getUrlWithOrderId(orderId: string): string {
  const searchParams = new URLSearchParams();
  searchParams.set(`orderId`, orderId);

  return `${window.location.pathname}?${searchParams.toString()}`;
}

export function useOrderList({ loading, orders, user }: UseOrderListParams): UseOrderListReturn {
  const { openModal } = useModals();
  const { push } = useRouter() ?? {};
  const showLoadingSpinner = loading || !orders;
  const orderId = new URLSearchParams(window.location.search).get(`orderId`);

  useEffect(() => {
    if (orderId) {
      user.viewOrderId = orderId;
      void openModal(ModalNames.orderStatus).then(() => {
        void push?.(window.location.pathname);
      });
    }
  }, [orderId, user, openModal, push]);

  const handleViewOrder = (order: ConsumerOrder, canViewStatus: boolean): void => {
    user.viewOrderId = order.orderId;
    if (canViewStatus && typeof order.orderId === 'string') {
      void push?.(getUrlWithOrderId(order.orderId));
    } else {
      void openModal(ModalNames.orderReceipt);
    }
  };

  return {
    viewableStatuses: VIEWABLE_STATUSES,
    showLoadingSpinner,
    handleViewOrder,
  };
}
